/**
 * The `projects` namespace, in English — B1 (the projects list) and B2 (the new-project
 * drawer over it), KLQ-20260819-003.
 *
 * Its own namespace rather than more of `app`: namespaces are split by screen family so a
 * language pack can ship a family at a time (`lib/i18n/catalog.ts`), and the chrome above
 * this screen is read by every family while these strings are read by one.
 *
 * **No message here invents a number.** `GET /api/v1/projects` returns no totals, so the two
 * count messages are only ever rendered once every page has been loaded — see `ProjectsPage`.
 */

export const projectsEn = {
  'page.title': 'Projects',
  // Rendered only when the last page has been loaded, and counted from what is on screen.
  'page.count.split': '{active} active · {archived} archived',
  'page.count.total': '{count, plural, one {# project} other {# projects}}',

  'action.newProject': 'New project',
  'action.createFirst': 'Create your first project',
  'action.loadMore': 'Load more',
  'action.clearFilters': 'Show all projects',
  'action.retry': 'Try again',

  'filter.status': 'Status',
  'filter.all': 'All',
  'filter.active': 'Active',
  'filter.archived': 'Archived',
  'filter.relation': 'Ownership',
  'filter.owned': 'Owned by us',
  'filter.shared': 'Shared with us',

  'sort.label': 'Sort',
  'sort.lastActivity': 'Last activity',
  'sort.name': 'Name',
  'sort.created': 'Created',

  'card.relation.owned': 'Owned',
  'card.relation.shared': 'Shared with us',
  'card.updated': 'Updated {when}',
  // Between client and address on the card caption. Punctuation, kept in the catalog so a
  // language that separates differently can say so.
  'card.separator': ' · ',

  'status.loading': 'Loading projects…',
  'empty.title': 'No projects yet',
  'empty.body': 'A project holds the drawings, reviews and tasks for one building.',
  'emptyFiltered.title': 'Nothing matches these filters',
  'emptyFiltered.body': 'Try a wider status, or drop the ownership filter.',
  'error.load': 'The project list could not be loaded.',

  'drawer.title': 'New project',
  'drawer.subtitle': 'Name it now; everything else can be filled in later.',
  'drawer.field.name.label': 'Project name',
  'drawer.field.name.placeholder': 'Riverside Residences',
  // Only drawn for somebody who works for more than one studio, which is why it does not
  // explain what a workspace is — the people who see it have two.
  'drawer.field.workspace.label': 'Studio',
  'drawer.field.workspace.hint': 'Which of your studios owns this project. It cannot be moved later.',
  'drawer.field.currentPhase.label': 'Starting stage',
  'drawer.field.currentPhase.hint': 'New drawings are filed here until you move the project on.',
  'drawer.action.create': 'Create project',
  'drawer.action.cancel': 'Cancel',
  'drawer.action.close': 'Close',

  'drawer.error.nameRequired': 'Enter a name for the project.',
  'drawer.error.nameTooLong': 'Keep the name to {max} characters or fewer.',
  'drawer.error.nameTaken': 'Your studio already has a project with this name.',
  'drawer.error.forbidden': 'You are not allowed to create projects in this studio.',
  'drawer.error.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'drawer.error.validation': 'Check the fields and try again.',
  'drawer.error.unexpected': 'The project could not be created. Try again.',

  // The phase bar above the project tabs (KLQ-20260827-002).
  'phase.bar.label': 'Project stage',
  'phase.all': 'All',
  // What the dot on a chip means. It is the project's default for new drawings, which is a
  // different fact from what the viewer is looking at, so it is never worded as a selection.
  'phase.current': 'New drawings are filed here.',
  'phase.archived': '(archived)',
  'phase.menu.label': 'Actions for {phase}',
  'phase.action.add': 'Add stage',
  'phase.action.setCurrent': 'File new drawings here',
  'phase.action.rename': 'Rename…',
  'phase.action.archive': 'Archive',
  'phase.action.restore': 'Restore',
  'phase.action.remove': 'Remove',

  'phase.add.title': 'Add a stage',
  'phase.add.subtitle': 'Stages come from the standard this project follows.',
  'phase.add.submit': 'Add stage',
  'phase.rename.title': 'Rename stage',
  'phase.rename.subtitle': 'Only the name changes. The stage keeps its place in the order.',
  'phase.rename.submit': 'Save name',
  'phase.field.definition.label': 'Stage',
  'phase.field.definition.hint':
    "Only stages from this project's standard can be added.",
  'phase.group.available': 'Not yet added',
  'phase.group.another': 'Another round of',
  'phase.field.label.label': 'Name',
  'phase.field.label.placeholder': 'Leave empty to use the standard name',
  'phase.field.label.hint': 'Optional. Use it when your practice calls this stage something else.',
  'phase.hint.labelRequired': 'This project already has this stage. Give the second one a name that tells them apart.',
  'phase.hint.noLadder': 'This project has no standard pinned, so there are no stages to add.',
  'phase.error.pickOne': 'Choose a stage to add.',
  'phase.error.labelRequired': 'Give this stage a name. The project already has one like it.',
  'phase.error.inUse': 'Drawings are filed under this stage, so it was archived instead of removed.',
  'phase.error.forbidden': "You are not allowed to change this project's stages.",
  'phase.error.unexpected': 'The stage could not be changed. Try again.',

  'stub.back': 'Back to projects',
  'stub.body': 'The project screens are not built yet.',
  'stub.missing': 'That project could not be opened.',
} as const;

export type ProjectsMessageKey = keyof typeof projectsEn;
